import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatRupiahFull } from "@/lib/format";
import { Plus, Edit2, Trash2, Wallet, ChevronRight } from "lucide-react";
import RekeningForm from "@/pages/forms/RekeningForm";
import DeleteConfirm from "@/components/DeleteConfirm";
import { toast } from "sonner";

export default function Rekening() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rekening");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doDelete = async () => {
    try {
      await api.delete(`/rekening/${confirmDel.id}`);
      toast.success("Rekening dihapus");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  const totalSaldo = items.reduce((a, b) => a + (b.saldo || 0), 0);

  return (
    <div className="pb-40" data-testid="rekening-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: "#DEEAF1", color: "#1F4E5F" }}
        >
          <Wallet size={20} />
        </div>
        <div>
          <div className="text-xs text-slate-500">Total saldo semua rekening</div>
          <div className="text-lg font-extrabold text-slate-900" data-testid="rekening-total-saldo">
            {formatRupiahFull(totalSaldo)}
          </div>
        </div>
      </div>

      <div className="px-4 space-y-2">
        {loading ? (
          <div className="text-xs text-slate-500 py-8 text-center">
            Memuat...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-xs text-slate-500 border border-slate-100">
            Belum ada rekening. Tambahkan rekening pertama Anda.
          </div>
        ) : (
          items.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-2xl p-4 border border-slate-100 tap cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => nav(`/rekening/${r.id}/mutasi`)}
              data-testid={`rekening-row-${r.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">
                    {r.bank}
                  </div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 truncate">
                    {r.nama}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div
                    className="text-base font-extrabold"
                    style={{ color: "#1F4E5F" }}
                  >
                    {formatRupiahFull(r.saldo)}
                  </div>
                  <ChevronRight size={14} className="text-slate-400" />
                </div>
              </div>
              <div className="flex gap-1.5 mt-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(r);
                    setFormOpen(true);
                  }}
                  data-testid={`rekening-edit-${r.id}`}
                  className="text-[10px] px-2 py-1 rounded-md bg-slate-100 text-slate-600 font-semibold tap flex items-center gap-1"
                >
                  <Edit2 size={10} /> Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDel(r);
                  }}
                  data-testid={`rekening-delete-${r.id}`}
                  className="text-[10px] px-2 py-1 rounded-md font-semibold tap flex items-center gap-1"
                  style={{ backgroundColor: "#FCE4E4", color: "#C00000" }}
                >
                  <Trash2 size={10} /> Hapus
                </button>
                <div className="ml-auto text-[10px] font-semibold text-[#1F4E5F] self-center">
                  Lihat mutasi →
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <button
        onClick={() => {
          setEditing(null);
          setFormOpen(true);
        }}
        data-testid="tambah-rekening-btn"
        className="fixed left-1/2 -translate-x-[50%] w-full max-w-[380px] px-4"
        style={{ bottom: "80px" }}
      >
        <div
          className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-white font-semibold text-sm shadow-lg tap"
          style={{ backgroundColor: "#1F4E5F" }}
        >
          <Plus size={16} /> Tambah Rekening
        </div>
      </button>

      <RekeningForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        existing={editing}
        onSaved={load}
      />
      <DeleteConfirm
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={doDelete}
        itemName={confirmDel?.nama}
        testId="delete-rekening-dialog"
      />
    </div>
  );
}
